interface CacheItem<T> {
    data: T;
    expiry: number;
}

interface CacheOptions {
    ttl?: number; // Time to live in milliseconds
    forceFresh?: boolean; // Force to get fresh data
}

class CacheService {
    private cache: Map<string, CacheItem<any>> = new Map();
    private defaultTTL = 3 * 60 * 1000; // 3 minutes in milliseconds

    /**
     * Get data from cache or fetch it using the provided function
     * @param key Cache key
     * @param fetchFn Function to fetch data if not in cache
     * @param options Cache options
     * @returns The cached or fresh data
     */
    async get<T>(
        key: string,
        fetchFn: () => Promise<T>,
        options: CacheOptions = {}
    ): Promise<T> {
        const { ttl = this.defaultTTL, forceFresh = false } = options;
        const now = Date.now();

        // Check if we have a valid cache entry
        if (!forceFresh) {
            const cachedItem = this.cache.get(key);
            if (cachedItem && cachedItem.expiry > now) {
                console.log(`Using cached data for key: ${key}`);
                return cachedItem.data as T;
            }
        }

        // Fetch fresh data
        // console.log(`Fetching fresh data for key: ${key}`);
        const data = await fetchFn();

        // Store in cache
        this.cache.set(key, {
            data,
            expiry: now + ttl
        });

        return data;
    }

    /**
     * Clear a specific cache entry
     * @param key Cache key to clear
     */
    clear(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Clear all cache entries
     */
    clearAll(): void {
        this.cache.clear();
    }

    /**
     * Set the default TTL for all cache entries
     * @param ttl Time to live in milliseconds
     */
    setDefaultTTL(ttl: number): void {
        this.defaultTTL = ttl;
    }

    /**
     * Get the default TTL
     * @returns Default TTL in milliseconds
     */
    getDefaultTTL(): number {
        return this.defaultTTL;
    }

    /**
     * Check if a key exists in the cache and is not expired
     * @param key Cache key
     * @returns Whether the key exists and is valid
     */
    has(key: string): boolean {
        const cachedItem = this.cache.get(key);
        return !!cachedItem && cachedItem.expiry > Date.now();
    }

    /**
     * Get all active cache keys
     * @returns Array of all active cache keys
     */
    getKeys(): string[] {
        const now = Date.now();
        const keys: string[] = [];

        this.cache.forEach((item, key) => {
            if (item.expiry > now) {
                keys.push(key);
            }
        });

        return keys;
    }
}

export const cacheService = new CacheService(); 